/**
 * Admin Cricket Transfer Panel — admin-only UI for performing a transfer on a
 * squad owner's behalf when the site is down or a user has texted in a swap.
 *
 * Reuse contract:
 *   - The actual transfer math, banking, validation and point-stability guards
 *     are owned by utils/cricketTransfer.computeCricketTransferUpdate — the SAME
 *     function the player-facing SquadSelectionPage uses. This panel only selects
 *     a league + squad, fetches the pool, opens the existing TransferModal, and
 *     on submit writes the computed payload with the history entry stamped with
 *     `actedByAdminUid`.
 *
 * Mirrors AdminTennisTransferPanel.tsx.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { League, LeagueSquad, Player, User } from '../../types/database';
import {
  squadService,
  playerPoolService,
  leaderboardSnapshotService,
  userService,
} from '../../services/firestore';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCricketLeagueOptionsForAdminTransfer,
  getCricketSquadOptionsForAdminTransfer,
  mapCricketPoolToPlayers,
} from '../../utils/adminCricketTransfer';
import { computeCricketTransferUpdate } from '../../utils/cricketTransfer';
import TransferModal, { TransferData } from '../squad/TransferModal';

type Props = {
  leagues: League[];
};

const AdminCricketTransferPanel: React.FC<Props> = ({ leagues }) => {
  const { user } = useAuth();

  const cricketLeagues = useMemo(
    () => getCricketLeagueOptionsForAdminTransfer(leagues),
    [leagues],
  );

  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedSquadId, setSelectedSquadId] = useState<string>('');
  const [squads, setSquads] = useState<LeagueSquad[]>([]);
  const [usersByUid, setUsersByUid] = useState<Record<string, User>>({});
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);

  const selectedLeague = useMemo(
    () => cricketLeagues.find((l) => l.id === selectedLeagueId) ?? null,
    [cricketLeagues, selectedLeagueId],
  );

  // Load squads + pool + user names whenever the selected league changes
  useEffect(() => {
    let cancelled = false;
    if (!selectedLeague) {
      setSquads([]);
      setUsersByUid({});
      setAvailablePlayers([]);
      setSelectedSquadId('');
      return;
    }
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const [squadList, pool] = await Promise.all([
          squadService.getByLeague(selectedLeague.id),
          selectedLeague.playerPoolId
            ? playerPoolService.getById(selectedLeague.playerPoolId)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setSquads(squadList);
        setAvailablePlayers(pool ? mapCricketPoolToPlayers(pool.players) : []);
        const uniqueUids = Array.from(new Set(squadList.map((s) => s.userId)));
        const users = await userService.getByIds(uniqueUids);
        if (cancelled) return;
        const map: Record<string, User> = {};
        users.forEach((u) => { map[u.uid] = u; });
        setUsersByUid(map);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : 'Failed to load squads for league.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedLeague]);

  const squadOptions = useMemo(
    () => (selectedLeague ? getCricketSquadOptionsForAdminTransfer(squads, selectedLeague) : []),
    [squads, selectedLeague],
  );

  const selectedOption = squadOptions.find((o) => o.squad.id === selectedSquadId) ?? null;
  const selectedSquad = selectedOption?.squad ?? null;

  const targetUserName = useMemo(() => {
    if (!selectedSquad) return undefined;
    const u = usersByUid[selectedSquad.userId];
    return u?.displayName || u?.email || selectedSquad.squadName;
  }, [selectedSquad, usersByUid]);

  const canOpen = Boolean(
    user && selectedLeague && selectedSquad && selectedOption && !selectedOption.isDisabled,
  );

  // Drives the existing TransferModal. Runs the SAME computation the player path
  // runs, then writes the payload (audit-stamped) and refreshes the leaderboard.
  const handleSubmitTransfer = async (transferData: TransferData) => {
    if (!user || !selectedLeague || !selectedSquad) return;

    const { updatePayload } = computeCricketTransferUpdate({
      existingSquad: selectedSquad,
      transferData,
      availablePlayers,
      league: selectedLeague,
      actedByAdminUid: user.uid,
    });

    await squadService.update(selectedSquad.id, updatePayload as Partial<LeagueSquad>);

    // Refresh leaderboard snapshot (best-effort, mirrors the player path).
    try {
      await leaderboardSnapshotService.create(selectedLeague.id);
    } catch (snapshotError) {
      console.error('Error creating leaderboard snapshot:', snapshotError);
    }

    // Reload squads so transfer counts reflect this change for any follow-up.
    const refreshed = await squadService.getByLeague(selectedLeague.id);
    setSquads(refreshed);
    setSuccess(`Transfer applied for ${targetUserName ?? selectedSquad.squadName}.`);
    setModalOpen(false);
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Admin Transfer — On Behalf
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick a cricket league and a squad, then perform a transfer through the
        same flow the player would use. The transfer is audit-stamped with your
        admin UID and runs the identical banking and point-stability logic.
        Squads with zero transfers remaining cannot be selected.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth disabled={cricketLeagues.length === 0}>
          <InputLabel>Cricket League</InputLabel>
          <Select
            label="Cricket League"
            value={selectedLeagueId}
            onChange={(e) => {
              setSelectedLeagueId(e.target.value);
              setSelectedSquadId('');
              setSuccess('');
            }}
          >
            <MenuItem value="">— Select league —</MenuItem>
            {cricketLeagues.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!selectedLeague || loading}>
          <InputLabel>Squad</InputLabel>
          <Select
            label="Squad"
            value={selectedSquadId}
            onChange={(e) => { setSelectedSquadId(e.target.value); setSuccess(''); }}
          >
            <MenuItem value="">— Select squad —</MenuItem>
            {squadOptions.map((opt) => {
              const owner = usersByUid[opt.squad.userId];
              const ownerLabel = owner?.displayName || owner?.email || 'Unknown user';
              return (
                <MenuItem
                  key={opt.squad.id}
                  value={opt.squad.id}
                  disabled={opt.isDisabled}
                >
                  {opt.squad.squadName} · {ownerLabel} — {opt.transfersLeft} left
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          disabled={!canOpen}
          onClick={() => { setSuccess(''); setModalOpen(true); }}
        >
          Open Transfer Flow
        </Button>
        {loading && <CircularProgress size={20} />}
        {selectedOption?.isDisabled && (
          <Typography variant="caption" color="warning.main">
            This squad has no transfers remaining.
          </Typography>
        )}
      </Box>

      {user && selectedLeague && selectedSquad && (
        <TransferModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          league={selectedLeague}
          existingSquad={selectedSquad}
          availablePlayers={availablePlayers}
          onSubmitTransfer={handleSubmitTransfer}
          adminContext={{ actingUid: user.uid, targetUserName }}
        />
      )}
    </Paper>
  );
};

export default AdminCricketTransferPanel;
